import { InputAdornment, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import React, { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiButton from 'web-library/stories/atom/Button/ApiButton';
import Button from 'web-library/stories/atom/Button/Button';
import Grid from 'web-library/stories/atom/Grid/Grid';
import Typography from 'web-library/stories/atom/Typography/Typography';
import Icon, { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useHistory } from 'react-router-dom';
import { useGetCaptchaSiteKey } from '../../../api-refactored/user/useGetCaptchaSiteKey';
import { usePostCaptchaVerifyToken } from '../../../api-refactored/user/usePostCaptchaVerifyToken';
import { UseLoginFormReturn } from '../logic/useLoginForm';
import RouteKey from '../../../routes/key';
import { TextFieldForm } from 'web-library';

const Captcha = lazy(() => import('web-library/stories/atom/Captcha/Captcha'));

const useStyles = makeStyles()((theme) => ({
  formContainer: {
    paddingBottom: theme.spacing(3),
  },
  formItem: {
    paddingBottom: theme.spacing(2),
  },
  bottomContainer: {
    alignItems: 'center',
  },
  button: {
    height: 'fit-content',
  },
}));

const { createAccount } = RouteKey.auth.child;

interface ExistingAccountLoginFormProps {
  loginForm: UseLoginFormReturn;
}

const ExistingAccountLoginForm = ({
  loginForm: { login, form, countDownObj, loginMutation, showCaptcha },
}: ExistingAccountLoginFormProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const history = useHistory();
  const {
    handleSubmit,
    formState: { errors },
  } = form;
  const [isBot, setIsBot] = useState<boolean>(false);
  const checkIsBot = (result: boolean) => setIsBot(result);
  const captchaVerifyTokenMutation = usePostCaptchaVerifyToken({
    onSuccess: (data) => setIsBot(data ? data.isBot : true),
    onError: () => setIsBot(true),
  });

  return (
    <form className={classes.formContainer} onSubmit={handleSubmit(login)}>
      {/* [EPPOPNKHJV-2678]: WOGAA Accessibility: fix "Elements with visible text labels do not have matching accessible names";
          let the visible Username and Password labels name their respective inputs. */}
      <TextFieldForm
        form={form}
        label={t('profile->>LoginForm.Username')}
        className={classes.formItem}
        error={!!errors.username}
        type="text"
        name="username"
        value={form.getValues('username')}
        inputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon name={IconName.user} color={theme.color.icon.iconDefault} />
            </InputAdornment>
          ),
        }}
        placeholder={t('profile->>LoginForm.Username')}
        helperText={errors?.username?.message?.toString()}
      />

      <TextFieldForm
        label={t('profile->>LoginForm.Password')}
        className={classes.formItem}
        error={!!errors.password}
        helperText={errors?.password?.message?.toString()}
        type="password"
        name="password"
        value={form.getValues('password')}
        inputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon name={IconName.padLock} color={theme.color.icon.iconDefault} />
            </InputAdornment>
          ),
        }}
        autoComplete="current-password"
        id="current-password"
        placeholder={t('profile->>LoginForm.Password')}
        isInputTextFree
        form={form}
      />

      {showCaptcha && (
        <Suspense fallback={null}>
          <Captcha
            {...{
              id: 'login-captcha',
              className: classes.formItem,
              checkIsBot,
              captchaSiteKeyMutation: useGetCaptchaSiteKey,
              captchaVerifyTokenMutation,
            }}
          />
        </Suspense>
      )}

      <Grid container className={classes.bottomContainer} spacing={2}>
        <Grid>
          <ApiButton
            status={loginMutation.status}
            type="submit"
            id="login-button"
            disabled={!countDownObj.countDownEnded || isBot}
            aria-label="Login"
          >
            {t('profile->>LoginForm.Login')}
          </ApiButton>
        </Grid>
        <Grid>
          <Button
            aria-label={t('profile->>LoginForm.CreateAnAccount')}
            variant="outlined"
            id="button-create-account"
            className={classes.button}
            onClick={() => history.push(`./${createAccount}`)}
          >
            <Typography variant="inherit">{t('profile->>LoginForm.CreateAnAccount')}</Typography>
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ExistingAccountLoginForm;
